import { useCallback, useContext, useEffect, useState } from 'react';
import empresasService from 'services/empresas';
import { DICTONARY } from 'const/Dictonary';
import { AuthContext } from 'context';
import { AlertaExito } from 'utils/Alertas';
import { PERMISOS } from 'const/Permisos';

export const useSucursales = codigoEmpresa => {
  const { perfiles, isTienePermisos } = useContext(AuthContext);
  const isSuperAdmin = perfiles.isSuperAdmin;
  const [isLoading, setIsLoading] = useState(false);
  const [sucursales, setSucursales] = useState([]);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [isLoadingEmpresas, setIsLoadingEmpresas] = useState(false);
  // Grupos
  const [grupoSeleccionado, setGrupoSeleccionado] = useState(null);
  const [grupos, setGrupos] = useState([]);
  const [isLoadingGrupos, setIsLoadingGrupos] = useState(false);

  const cargarListaSucursales = useCallback(async () => {
    if (
      (isTienePermisos(PERMISOS.administracionSucursales) ||
        isTienePermisos(PERMISOS.sucursales)) &&
      !empresaSeleccionada &&
      !grupoSeleccionado
    ) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await empresasService.obtenerListaSucursales({
        codigoEmpresa: empresaSeleccionada?.codigoEmpresa,
        codigoGrupo: grupoSeleccionado?.codigoGrupo || null,
        asociados: isSuperAdmin ? false : true //si es super admin false para obtener todo, si no sucursales con permiso
      });

      if (response.data.error) {
        throw new Error('ERROR_AL_CARGAR_SUCURSALES');
      }
      setSucursales(response.data);
    } catch (error) {
      console.error('Error al obtener las sucursales:', error);
      setSucursales([]);
    } finally {
      setIsLoading(false);
    }
  }, [empresaSeleccionada, codigoEmpresa, grupoSeleccionado]);

  const cargarSucursales = async () => {
    setIsLoading(true);

    try {
      if (isNaN(codigoEmpresa) && isNaN(empresaSeleccionada?.codigoEmpresa)) {
        setIsLoading(false);
        return;
      }

      const response = await empresasService.obtieneSucursales({
        codigoEmpresa: empresaSeleccionada
          ? empresaSeleccionada.codigoEmpresa
          : codigoEmpresa,
        totalPagina: DICTONARY.PAGINACION.ALL_DATA
      });

      const error = response.data.error;
      if (error) throw new Error('ERROR_AL_CARGAR_SUCURSALES');

      setSucursales(response.data.content);
    } catch (error) {
      setSucursales([]);
      console.log('Error al obtener las sucursales ', error);
    } finally {
      setIsLoading(false);
    }
  };

  const cargarListaEmpresas = useCallback(async () => {
    setIsLoadingEmpresas(true);
    try {
      const response = await empresasService.obtenerListaEmpresas({
        codigoGrupo: grupoSeleccionado?.codigoGrupo,
        asociados: isSuperAdmin ? false : true //solo buscar negocios con permiso en sus sucursales
      });
      // console.log('response', response);
      if (response.data.error) {
        throw new Error('ERROR_AL_CARGAR_EMPRESAS');
      }
      const empresasAux = response.data.map(empresa => ({
        codigoEmpresa: empresa.codigoEmpresa,
        nombreEmpresa: empresa.nombreEmpresa
      }));

      setEmpresas(empresasAux);
    } catch (error) {
      console.error('Error al cargar las empresas:', error);
      setEmpresas([]);
    } finally {
      setIsLoadingEmpresas(false);
    }
  }, [codigoEmpresa, grupoSeleccionado]);

  const cargarListaGrupos = useCallback(async () => {
    setIsLoadingGrupos(true);
    try {
      const response = await empresasService.obtenerListaGrupos();

      if (response.data.error) {
        throw new Error('ERROR_AL_CARGAR_GRUPOS');
      }
      setGrupos(response.data);
    } catch (error) {
      console.error('Error al cargar los grupos:', error);
      setGrupos([]);
    } finally {
      setIsLoadingGrupos(false);
    }
  }, []);

  useEffect(() => {
    if (
      empresaSeleccionada &&
      empresaSeleccionada.codigoEmpresa === codigoEmpresa &&
      sucursales.length > 0
    ) {
      return;
    }
    isTienePermisos(PERMISOS.administracionSucursales) ||
    isTienePermisos(PERMISOS.sucursales)
      ? cargarListaSucursales()
      : cargarSucursales();
  }, [empresaSeleccionada, grupoSeleccionado]);

  useEffect(() => {
    if (
      isTienePermisos(PERMISOS.administracionSucursales) ||
      isTienePermisos(PERMISOS.sucursales)
    ) {
      cargarListaEmpresas();
    }
  }, []);

  useEffect(() => {
    if (
      isTienePermisos(PERMISOS.administracionSucursales) ||
      isTienePermisos(PERMISOS.sucursales)
    ) {
      cargarListaGrupos();
    }
  }, []);

  //ELIMINACION DE SUCURSAL
  const [openConfirmacion, setOpenConfirmacion] = useState(false);
  const [sucursalEliminar, setSucursalEliminar] = useState({});

  const handleIniciarEliminacion = sucursal => {
    setSucursalEliminar(sucursal);
    setOpenConfirmacion(true);
  };

  const handleConfirmarEliminacion = async () => {
    try {
      const response = await empresasService.eliminarSucursal(
        sucursalEliminar.codigoSucursal
      );
      if (response.status === 200) {
        setSucursales(
          sucursales.filter(
            sucursal =>
              sucursal.codigoSucursal !== sucursalEliminar.codigoSucursal
          )
        );
        AlertaExito('Se ha eliminado correctamente la sucursal');
        setOpenConfirmacion(false);
        setSucursalEliminar({});
      }
    } catch (error) {
      console.log('Error en la confirmación ', error);
      setOpenConfirmacion(false);
    }
  };

  return {
    isLoading,
    isSuperAdmin,
    isLoadingEmpresas,
    isLoadingGrupos,

    sucursales,
    setSucursales,

    empresas,
    empresaSeleccionada,
    setEmpresaSeleccionada,

    openConfirmacion,
    setOpenConfirmacion,
    sucursalEliminar,
    handleIniciarEliminacion,
    handleConfirmarEliminacion,
    // Grupos
    grupos,
    grupoSeleccionado,
    setGrupoSeleccionado,
    cargarListaEmpresas
  };
};
